import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/api";

function initialFormState() {
  return {
    name: "",
    description: "",
    nameBg: "",
    descriptionBg: "",
    price: "",
    imageUrl: "",
  };
}

// This admin-only form always shows its field labels in English, deliberately not
// following the site's EN/BG visitor toggle: it's the one place both language
// versions of a product are entered side by side, so the labels need to stay
// unambiguous about which language field is which, regardless of admin's own
// language preference.
function ProductForm({ onSuccess, editingProduct, setEditingProduct }) {
  const [formData, setFormData] = useState(initialFormState());

  const resetForm = useCallback(() => {
    setFormData(initialFormState());
    if (typeof setEditingProduct === "function") {
      setEditingProduct(null);
    }
  }, [setEditingProduct]);

  useEffect(() => {
    if (editingProduct && typeof editingProduct === "object") {
      setFormData({
        name: editingProduct.name || "",
        description: editingProduct.description || "",
        nameBg: editingProduct.nameBg || "",
        descriptionBg: editingProduct.descriptionBg || "",
        price: editingProduct.price?.toString() || "",
        imageUrl: editingProduct.imageUrl || "",
      });
    } else {
      resetForm();
    }
  }, [editingProduct, resetForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
    };

    try {
      if (editingProduct) {
        await api.put(`/api/products/${editingProduct.id}`, payload);
      } else {
        await api.post("/api/products", payload);
      }

      if (typeof onSuccess === "function") {
        onSuccess(payload.name);
      }
      resetForm();
    } catch (error) {
      console.error(
        "Error saving product:",
        error.response?.data || error.message
      );
      alert("Failed to save product. Admin authorization required.");
    }
  };

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <h3>{editingProduct ? "Edit Product" : "Create New Product"}</h3>

      <input
        type="text"
        name="name"
        placeholder="Name (English)"
        value={formData.name}
        onChange={handleChange}
        required
      />
      <textarea
        name="description"
        placeholder="Description (English)"
        value={formData.description}
        onChange={handleChange}
        rows={3}
        required
      />

      <input
        type="text"
        name="nameBg"
        placeholder="Name (Bulgarian, optional)"
        value={formData.nameBg}
        onChange={handleChange}
      />
      <textarea
        name="descriptionBg"
        placeholder="Description (Bulgarian, optional)"
        value={formData.descriptionBg}
        onChange={handleChange}
        rows={3}
      />

      <input
        type="number"
        name="price"
        step="0.01"
        placeholder="Price"
        value={formData.price}
        onChange={handleChange}
        required
      />

      <input
        type="text"
        name="imageUrl"
        placeholder="Image URL (optional — shows a graded color swatch if left blank)"
        value={formData.imageUrl}
        onChange={handleChange}
      />

      <div className="form-buttons">
        {editingProduct && (
          <button type="button" onClick={resetForm} className="btn-secondary">
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary">
          {editingProduct ? "Update Product" : "Create Product"}
        </button>
      </div>
    </form>
  );
}

export default ProductForm;
